import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, query, orderBy, getDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  Container, Paper, Typography, TextField, Button, Grid, List, ListItem, 
  ListItemText, IconButton, Box, InputAdornment, Chip, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  MenuItem,
  Stack
} from '@mui/material';
import { Delete, Edit, AddCircle, EventBusy, ArrowBack, CheckCircleOutline, RadioButtonUnchecked, CheckCircle, Undo } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BancoSelectionDialog from './BancoSelectionDialog';

function GastosMensais({ user }) {
  const [gastos, setGastos] = useState([]);
  const [novoGasto, setNovoGasto] = useState({ nome: '', valorFormatado: '', dataVencimento: '', numParcelas: 1 });
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [bancos, setBancos] = useState([]);
  const [selectedGasto, setSelectedGasto] = useState(null);
  const [openBancoDialog, setOpenBancoDialog] = useState(false);
  const [gastosMes, setGastosMes] = useState({});
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [openDetalhes, setOpenDetalhes] = useState(false);
  const [gastoDetalhes, setGastoDetalhes] = useState(null);

  const [openNewExpense, setOpenNewExpense] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [gastoEditando, setGastoEditando] = useState(null);
  
  const navigate = useNavigate();

  const processarGastos = (gastosData, currentMonth) => {
    const agrupados = {};
    const months = new Set();

    gastosData.forEach(gasto => {
      if (gasto.parcelas && gasto.parcelas.length > 0) {
        gasto.parcelas.forEach(parcela => {
          
            const data = new Date(parcela.dataParcela + 'T12:00:00');
            const mesAno = `${data.toLocaleString('pt-BR', { month: 'long' }).toUpperCase()} DE ${data.getFullYear()}`;
            months.add(mesAno);
            if (!agrupados[mesAno]) {
              agrupados[mesAno] = [];
            }
            agrupados[mesAno].push({
              ...gasto,
              id: `${gasto.id}-${parcela.numero}`,
              valor: parcela.valor,
              dataVencimento: parcela.dataParcela,
              nome: `${gasto.nome} ${parcela.numero}/${gasto.numParcelas}`,
              status: parcela.paga ? 'pago' : 'pendente',
              parcelaInfo: parcela,
              gastoIdOriginal: gasto.id
            });
          
        });
      } else {
        const data = new Date(gasto.dataVencimento + 'T12:00:00');
        const mesAno = `${data.toLocaleString('pt-BR', { month: 'long' }).toUpperCase()} DE ${data.getFullYear()}`;
        months.add(mesAno);
        if (!agrupados[mesAno]) {
          agrupados[mesAno] = [];
        }
        agrupados[mesAno].push({ ...gasto, gastoIdOriginal: gasto.id });
      }
    });

    const sortedMonths = Array.from(months).sort((a, b) => {
      const dateA = new Date(a.replace('DE', '1,'));
      const dateB = new Date(b.replace('DE', '1,'));
      return dateB - dateA;
    });

    setAvailableMonths(sortedMonths);

    const sortedAgrupados = {};
    for (const month of sortedMonths) {
      sortedAgrupados[month] = agrupados[month].sort((a, b) => new Date(a.dataVencimento) - new Date(b.dataVencimento));
    }
  
    setGastosMes(sortedAgrupados);
    if (currentMonth) {
      setSelectedMonth(currentMonth);
    } else if (sortedMonths.length > 0) {
      setSelectedMonth(sortedMonths[0]);
    }
  };

  const fetchDados = async () => {
    if (!user || !user.uid) return;
    const q = query(collection(db, `users/${user.uid}/gastos_mensais`), orderBy('dataVencimento', 'asc'));
    const snap = await getDocs(q);
    const gastosData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setGastos(gastosData);
    const today = new Date();
    const currentMonth = `${today.toLocaleString('pt-BR', { month: 'long' }).toUpperCase()} DE ${today.getFullYear()}`;
    processarGastos(gastosData, currentMonth);
  };

  const fetchBancos = async () => {
    if (!user || !user.uid) return;
    const q = query(collection(db, `users/${user.uid}/bancos`));
    const snap = await getDocs(q);
    setBancos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => { 
    fetchDados();
    fetchBancos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleValorChange = (e, setter) => {
    let value = e.target.value.replace(/\D/g, "");
    const res = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(parseFloat(value) / 100);
    setter(res === "NaN" ? "" : res);
  };

  const addMonthsISO = (dateStr, months) => {
    const d = new Date(dateStr + 'T12:00:00');
    d.setMonth(d.getMonth() + months);
    return d.toISOString().split('T')[0];
  };

  const adicionar = async () => {
    const valorNum = parseFloat(novoGasto.valorFormatado.replace(/\./g, '').replace(',', '.'));
    const numParcelas = Math.max(1, parseInt(novoGasto.numParcelas) || 1);
    if (novoGasto.nome && valorNum && novoGasto.dataVencimento) {
      const totalCentavos = Math.round(valorNum * 100);
      const base = Math.floor(totalCentavos / numParcelas);
      const resto = totalCentavos % numParcelas;
      const parcelas = Array.from({ length: numParcelas }, (_, i) => {
        const valorParcelaCent = base + (i < resto ? 1 : 0);
        return {
          numero: i + 1,
          valor: valorParcelaCent / 100,
          dataParcela: addMonthsISO(novoGasto.dataVencimento, i),
          paga: false,
          dataPagamento: null
        };
      });

      await addDoc(collection(db, `users/${user.uid}/gastos_mensais`), { 
        nome: novoGasto.nome.trim(), 
        valor: totalCentavos / 100, 
        dataVencimento: novoGasto.dataVencimento,
        status: 'pendente',
        numParcelas,
        parcelas,
        valorPago: 0,
        dataCriacao: new Date()
      });
      setNovoGasto({ nome: '', valorFormatado: '', dataVencimento: '', numParcelas: 1 });
      setOpenNewExpense(false);
      fetchDados();
      setMensagem({ tipo: 'success', texto: 'Despesa adicionada!' });
      setTimeout(() => setMensagem({ tipo: '', texto: '' }), 3000);
    } else {
      setMensagem({ tipo: 'error', texto: 'Preencha todos os campos obrigatórios.' });
    }
  };

  const deletar = async (id) => {
    if(window.confirm("Excluir esta despesa?")) {
      await deleteDoc(doc(db, `users/${user.uid}/gastos_mensais/${id}`)); 
      fetchDados();
      setMensagem({ tipo: 'info', texto: 'Despesa excluída.' });
    }
  };

  const abrirEdicao = (gasto) => {
    const originalGasto = gastos.find(g => gasto.id.startsWith(g.id)) || gasto;
    setGastoEditando({
      ...originalGasto,
      valorFormatado: new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(originalGasto.valor)
    });
    setOpenEdit(true);
  };

  const salvarEdicao = async () => {
    const valorNum = parseFloat(gastoEditando.valorFormatado.replace(/\./g, '').replace(',', '.'));
    if (gastoEditando.nome && valorNum && gastoEditando.dataVencimento) {
      await updateDoc(doc(db, `users/${user.uid}/gastos_mensais/${gastoEditando.id}`), {
        ...gastoEditando,
        valor: valorNum
      });
      setOpenEdit(false); 
      fetchDados();
      setMensagem({ tipo: 'success', texto: 'Despesa atualizada com sucesso!' });
    }
  };

  const handleMarcarComoPago = (gasto) => {
    setSelectedGasto(gasto);
    setOpenBancoDialog(true);
  };

  const confirmarPagamento = async (bancoId) => {
    if (!bancoId) {
      setMensagem({ type: 'error', text: 'Por favor, selecione um banco.' });
      return;
    }
    
    const gastoRef = doc(db, `users/${user.uid}/gastos_mensais`, selectedGasto.id);
    try {
      const dataPagamentoIso = new Date().toISOString().split('T')[0];
      await updateDoc(gastoRef, { status: 'pago', dataPagamento: dataPagamentoIso, bancoPagamentoId: bancoId });

      const bancoRef = doc(db, `users/${user.uid}/bancos`, bancoId);
      const newMovimentacao = {
        titulo: `Pagamento de ${selectedGasto.nome}`,
        descricao: `Pagamento de ${selectedGasto.nome}`,
        tipo: 'saida',
        valor: -selectedGasto.valor,
        data: dataPagamentoIso,
        gastoId: selectedGasto.id.split('-')[0],
        bancoId
      };
      await addDoc(collection(db, `users/${user.uid}/bancos/${bancoId}/movimentacoes`), newMovimentacao);
      
      const bancoDoc = await getDoc(bancoRef);
      const bancoData = bancoDoc.data();
      await updateDoc(bancoRef, {
        saldo: bancoData.saldo - selectedGasto.valor
      });

      setOpenBancoDialog(false);
      fetchDados();
      setMensagem({ tipo: 'success', texto: 'Despesa marcada como paga e registrada no banco!' });
    } catch (error) {
      setMensagem({ tipo: 'error', texto: 'Erro ao atualizar despesa.' });
    }
  };

const getBancoNome = (id) => bancos.find((b) => b.id === id)?.nome || 'Nao informado';

const abrirDetalhes = (g) => {
  const original = gastos.find((item) => (g.gastoIdOriginal ? item.id === g.gastoIdOriginal : g.id.startsWith(item.id))) || g;
  const parcelaInfo = g.parcelaInfo || original?.parcelas?.find((p) => p.numero === (g.parcelaInfo && g.parcelaInfo.numero));
  setGastoDetalhes({
    nome: g.nome,
    valor: g.valor,
    status: g.status,
    dataVencimento: g.dataVencimento,
    dataPagamento: parcelaInfo?.dataPagamento || g.dataPagamento || original?.dataPagamento || null,
    bancoPagamentoId: parcelaInfo?.bancoPagamentoId || g.bancoPagamentoId || original?.bancoPagamentoId || null,
    parcelaLabel: parcelaInfo ? `${parcelaInfo.numero}/${original?.numParcelas || g.parcelaInfo?.numero || ''}` : null,
  });
  setOpenDetalhes(true);
};


const marcarParcelaPaga = async (gastoId, parcelaIndex, paga, bancoId) => {
    const gastoRef = doc(db, `users/${user.uid}/gastos_mensais`, gastoId);
    const gastoToUpdate = gastos.find(g => g.id === gastoId);
    if (!gastoToUpdate) {
        setMensagem({ tipo: 'error', texto: 'Gasto não encontrado para atualização.' });
        return;
    }
    
    // --- LÓGICA DE PAGAMENTO ---
    if (paga) {
        if (!bancoId) {
            const parcela = gastoToUpdate.parcelas[parcelaIndex];
            setSelectedGasto({
                id: gastoId,
                nome: `${gastoToUpdate.nome} - Parcela ${parcela.numero}`,
                valor: parcela.valor,
                parcelaInfo: parcela,
                parcelaIndex: parcelaIndex
            });
            setOpenBancoDialog(true);
            return; // Aguarda seleção do banco
        }

        try {
            const parcela = gastoToUpdate.parcelas[parcelaIndex];
            // 1. Adicionar movimentação no banco
            const bancoRef = doc(db, `users/${user.uid}/bancos`, bancoId);
            const dataPagamentoIso = new Date().toISOString().split('T')[0];
            const newMovimentacao = {
              titulo: `Pagamento de ${gastoToUpdate.nome} - Parcela ${parcela.numero}`,
              descricao: `Pagamento de ${gastoToUpdate.nome} - Parcela ${parcela.numero}`,
              tipo: 'saida',
              valor: -parcela.valor,
              data: dataPagamentoIso,
              gastoId: gastoId,
              parcelaIndex: parcelaIndex,
              bancoId
            };
            await addDoc(collection(db, `users/${user.uid}/bancos/${bancoId}/movimentacoes`), newMovimentacao);

            // 2. Atualizar saldo do banco
            const bancoDoc = await getDoc(bancoRef);
            const bancoData = bancoDoc.data();
            await updateDoc(bancoRef, { saldo: bancoData.saldo - parcela.valor });

            // 3. Atualizar o gasto/parcela
            const novasParcelas = [...gastoToUpdate.parcelas];
            novasParcelas[parcelaIndex] = { ...novasParcelas[parcelaIndex], paga: true, dataPagamento: dataPagamentoIso, bancoPagamentoId: bancoId };
            const valorPago = novasParcelas.reduce((acc, p) => p.paga ? acc + p.valor : acc, 0);
            const status = novasParcelas.every(p => p.paga) ? 'pago' : 'parcial';
            await updateDoc(gastoRef, { parcelas: novasParcelas, status, valorPago });
            
            setMensagem({ tipo: 'success', texto: 'Parcela marcada como paga!' });

        } catch (error) {
            console.error("Erro ao pagar parcela:", error);
            setMensagem({ tipo: 'error', texto: 'Erro ao processar pagamento.' });
        } finally {
            setOpenBancoDialog(false);
            fetchDados(); // Re-sincronizar estado
        }

    // --- LÓGICA DE REVERSÃO DE PAGAMENTO ---
    } else {
        const parcelaReverter = gastoToUpdate.parcelas[parcelaIndex];
        const bancoPagamentoId = parcelaReverter.bancoPagamentoId;

        if (!bancoPagamentoId) {
            setMensagem({ tipo: 'error', texto: 'Não foi possível reverter: ID do banco não encontrado.' });
            return;
        }

        try {
            // 1. Encontrar a movimentação original
            const movRef = collection(db, `users/${user.uid}/bancos/${bancoPagamentoId}/movimentacoes`);
            const q = query(movRef, where("gastoId", "==", gastoId), where("parcelaIndex", "==", parcelaIndex));
            const movSnap = await getDocs(q);

            if (movSnap.empty) {
                throw new Error("Movimentação do banco correspondente não encontrada.");
            }

            const movDoc = movSnap.docs[0];
            const movData = movDoc.data();
            const valorReverter = movData.valor; // é negativo

            // 2. Excluir a movimentação e atualizar o saldo do banco
            await deleteDoc(movDoc.ref);
            const bancoRef = doc(db, `users/${user.uid}/bancos`, bancoPagamentoId);
            const bancoDoc = await getDoc(bancoRef);
            const bancoData = bancoDoc.data();
            await updateDoc(bancoRef, { saldo: bancoData.saldo - valorReverter }); // Subtrai o valor negativo, somando-o de volta

            // 3. Atualizar o gasto/parcela
            const novasParcelas = [...gastoToUpdate.parcelas];
            novasParcelas[parcelaIndex] = { ...novasParcelas[parcelaIndex], paga: false, dataPagamento: null, bancoPagamentoId: null };
            const valorPago = novasParcelas.reduce((acc, p) => p.paga ? acc + p.valor : acc, 0);
            const status = valorPago > 0 ? 'parcial' : 'pendente';
            await updateDoc(gastoRef, { parcelas: novasParcelas, status, valorPago });
            
            setMensagem({ tipo: 'success', texto: 'Pagamento revertido com sucesso!' });

        } catch (error) {
            console.error("Erro ao reverter pagamento:", error);
            setMensagem({ tipo: 'error', texto: `Erro ao reverter pagamento: ${error.message}` });
        } finally {
            fetchDados(); // Re-sincronizar estado
        }
    }
};

const reverterPagamentoGastoUnico = async (gasto) => {
    const bancoPagamentoId = gasto.bancoPagamentoId;
    if (!bancoPagamentoId) {
      setMensagem({ tipo: 'error', texto: 'Não foi possível reverter: ID do banco de pagamento não encontrado na despesa.' });
      return;
    }

    try {
      // 1. Encontrar a movimentação original no banco
      const movRef = collection(db, `users/${user.uid}/bancos/${bancoPagamentoId}/movimentacoes`);
      const q = query(movRef, where("gastoId", "==", gasto.id));
      const movSnap = await getDocs(q);

      if (movSnap.empty) {
        throw new Error("Movimentação do banco correspondente não encontrada para esta despesa.");
      }

      // Assumindo que só há uma movimentação por gasto único
      const movDoc = movSnap.docs[0];
      const valorReverter = movDoc.data().valor; // Valor é negativo

      // 2. Excluir a movimentação e atualizar o saldo do banco
      await deleteDoc(movDoc.ref);
      const bancoRef = doc(db, `users/${user.uid}/bancos`, bancoPagamentoId);
      const bancoDoc = await getDoc(bancoRef);
      const bancoData = bancoDoc.data();
      await updateDoc(bancoRef, { saldo: bancoData.saldo - valorReverter }); // Subtrai o valor negativo, efetivamente somando

      // 3. Atualizar o status da despesa para 'pendente'
      const gastoRef = doc(db, `users/${user.uid}/gastos_mensais`, gasto.id);
      await updateDoc(gastoRef, {
        status: 'pendente',
        dataPagamento: null,
        bancoPagamentoId: null
      });

      setMensagem({ tipo: 'success', texto: 'Pagamento revertido e despesa marcada como pendente!' });
    } catch (error) {
      console.error("Erro ao reverter pagamento de gasto único:", error);
      setMensagem({ tipo: 'error', texto: `Erro ao reverter: ${error.message}` });
    } finally {
      setOpenEdit(false);
      fetchDados();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return '#ff9800';
      case 'parcial': return '#2196f3';
      case 'pago': return '#4caf50';
      default: return '#9e9e9e';
    }
  };
  
  const formatarData = (valor) => valor ? new Date(valor + 'T12:00:00').toLocaleDateString('pt-BR') : 'Nao informado';

    const total = gastos.reduce((acc, i) => {
    // Para despesas já pagas, não somamos nada.
    if (i.status === 'pago') {
      return acc;
    }
    // Para despesas pendentes ou parciais, somamos o valor que ainda falta pagar.
    const valorRestante = i.valor - (i.valorPago || 0);
    return acc + valorRestante;
  }, 0);

  return (
    <Container maxWidth="md" sx={{ mt: 1, px: { xs: 0.5, sm: 2 } }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mb: 2, color: '#1a237e', fontWeight: 'bold' }}>Voltar</Button>
      {/* <FloatingCalculator /> */}

      {mensagem.texto && <Alert severity={mensagem.tipo} sx={{ mb: 2 }} onClose={() => setMensagem({ tipo: '', texto: '' })}>{mensagem.texto}</Alert>}
      
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#fff', border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
        <Box>
           <Typography variant="h6" fontWeight="bold" color="#1a237e" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>Minhas Despesas</Typography>
           <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Planejamento de Contas</Typography>
        </Box>
        <Box textAlign={{ xs: 'center', sm: 'right' }}>
          <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>Total Previsto</Typography>
          <Typography variant="h5" color="error" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: '#f5f5f5' }}>
        <Button fullWidth variant="outlined" onClick={() => setOpenNewExpense(true)} sx={{ py: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', borderColor: '#1a237e', color: '#1a237e' }}>
          <AddCircle fontSize="small" /> NOVA CONTA
        </Button>
      </Paper>
      
      <Box sx={{ mb: 2 }}>
        <TextField
          select
          fullWidth
          label="Selecione o Mês"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          variant="outlined"
        >
          {availableMonths.map(month => (
            <MenuItem key={month} value={month}>
              {month}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        <List sx={{ p: 0 }}>
          {gastosMes[selectedMonth] ? (
            gastosMes[selectedMonth].map((g, index) => {
              const dataObj = new Date(g.dataVencimento + 'T12:00:00');
              const hoje = new Date();
              const vencida = new Date(g.dataVencimento) < new Date(hoje.toISOString().split('T')[0]) && g.status !== 'pago';

              return (
                <React.Fragment key={g.id}>
                  <ListItem sx={{ py: 1, px: { xs: 1, sm: 2 }, '&:hover': { bgcolor: '#f9f9f9' }, bgcolor: g.status === 'pago' ? '#e8f5e9' : 'transparent' }} onClick={() => abrirDetalhes(g)}>
                    <Box sx={{ mr: 1, p: 0.75, borderRadius: 1, bgcolor: vencida ? '#ffebee' : '#e3f2fd', color: vencida ? '#d32f2f' : '#1565c0', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 45 }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ lineHeight: 0.9, fontSize: '0.65rem' }}>{dataObj.toLocaleString('pt-BR', { month: 'short' }).toUpperCase()}</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1 }}>{dataObj.getDate()}</Typography>
                    </Box>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight="bold">{g.nome}</Typography>}
                      secondary={<Chip label={g.status || 'pendente'} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: getStatusColor(g.status), color: 'white' }} />}
                    />
                    <Typography variant="body2" fontWeight="bold" color="#333" sx={{ mr: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>R$ {g.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {g.status !== 'pago' && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMarcarComoPago(g); }} title="Marcar como pago">
                          <CheckCircle sx={{ color: '#fb8c00' }} />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); abrirEdicao(g); }}>
                        <Edit fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); deletar(g.id.split('-')[0]); }}>
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </ListItem>
                  {index < gastosMes[selectedMonth].length - 1 && <Divider />}
                </React.Fragment>
              );
            })
          ) : (
            <Box sx={{ p: 5, textAlign: 'center' }}><EventBusy sx={{ fontSize: 40, color: '#ccc', mb: 1 }} /><Typography color="textSecondary">Nenhuma conta para este mês.</Typography></Box>
          )}
        </List>
      </Paper>
      <Dialog open={openDetalhes} onClose={() => setOpenDetalhes(false)} fullWidth maxWidth="sm">
        <DialogTitle>Detalhes da Conta</DialogTitle>
        <DialogContent dividers>
          {gastoDetalhes && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight="bold">{gastoDetalhes.nome}</Typography>
              {gastoDetalhes.parcelaLabel && (<Chip size="small" label={`Parcela ${gastoDetalhes.parcelaLabel}`} sx={{ width: 'fit-content' }} />)}
              <Typography>Valor: R$ {gastoDetalhes.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Typography>
              <Typography>Status: {gastoDetalhes.status}</Typography>
              <Typography>Vencimento: {formatarData(gastoDetalhes.dataVencimento)}</Typography>
              <Typography>Pago em: {gastoDetalhes.dataPagamento ? formatarData(gastoDetalhes.dataPagamento) : 'Ainda nao pago'}</Typography>
              <Typography>Banco: {gastoDetalhes.bancoPagamentoId ? getBancoNome(gastoDetalhes.bancoPagamentoId) : 'Nao informado'}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetalhes(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <BancoSelectionDialog
        open={openBancoDialog}
        onClose={() => setOpenBancoDialog(false)}
        bancos={bancos}
        onConfirm={(bancoId) => {
          if (selectedGasto.parcelaInfo) { // It's an installment
            const originalGasto = gastos.find(g => selectedGasto.id.startsWith(g.id));
            const parcelaIndex = originalGasto.parcelas.findIndex(p => p.numero === selectedGasto.parcelaInfo.numero);
            marcarParcelaPaga(originalGasto.id, parcelaIndex, true, bancoId);
          } else { // It's a single expense
            confirmarPagamento(bancoId);
          }
        }}
      />
      {/* Janela de Edição */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white' }}>Editar Despesa</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {gastoEditando && <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}><TextField fullWidth label="Descrição" value={gastoEditando.nome} onChange={(e) => setGastoEditando({...gastoEditando, nome: e.target.value})} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="date" label="Vencimento" value={gastoEditando.dataVencimento} onChange={(e) => setGastoEditando({...gastoEditando, dataVencimento: e.target.value})} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Valor" value={gastoEditando.valorFormatado} onChange={(e) => handleValorChange(e, (v) => setGastoEditando({...gastoEditando, valorFormatado: v}))} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            
            {gastoEditando.parcelas && gastoEditando.parcelas.length > 1 ? (
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#1a237e' }}>
                  Parcelas ({gastoEditando.parcelas.filter(p => p.paga).length}/{gastoEditando.parcelas.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {gastoEditando.parcelas.map((parcela, idx) => (
                    <Box key={idx} sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Parcela {parcela.numero}: R$ {parcela.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Vencimento: {new Date(parcela.dataParcela).toLocaleDateString('pt-BR')}
                          {parcela.paga && ` • Pago em: ${new Date(parcela.dataPagamento).toLocaleDateString('pt-BR')}`}
                        </Typography>
                      </Box>
                      <Button
                        variant={parcela.paga ? "contained" : "outlined"}
                        size="small"
                        color={parcela.paga ? "success" : "warning"}
                        startIcon={parcela.paga ? <CheckCircleOutline /> : <RadioButtonUnchecked />}
                        onClick={() => marcarParcelaPaga(gastoEditando.id, idx, !parcela.paga)}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        {parcela.paga ? 'Paga' : 'Pagar'}
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Grid>
            ) : (
                <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#1a237e' }}>
                        Status do Pagamento
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                            Status: <Chip component="span" label={gastoEditando.status || 'pendente'} size="small" sx={{ bgcolor: getStatusColor(gastoEditando.status), color: 'white' }} />
                        </Typography>
                        {gastoEditando.status === 'pago' ? (
                            <Button
                                variant="outlined"
                                color="warning"
                                startIcon={<Undo />}
                                onClick={() => reverterPagamentoGastoUnico(gastoEditando)}
                                sx={{ textTransform: 'none' }}
                            >
                                Reverter para Pendente
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircle />}
                                onClick={() => handleMarcarComoPago(gastoEditando)}
                                sx={{ textTransform: 'none' }}
                            >
                                Marcar como Pago
                            </Button>
                        )}
                    </Box>
                </Grid>
            )}

          </Grid>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarEdicao} sx={{ bgcolor: '#1a237e' }}>Salvar Alterações</Button>
        </DialogActions>
      </Dialog>

      {/* Janela de Nova Despesa */}
      <Dialog open={openNewExpense} onClose={() => setOpenNewExpense(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white' }}>Nova Conta</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}><TextField fullWidth label="Nome da Despesa" value={novoGasto.nome} onChange={(e) => setNovoGasto({...novoGasto, nome: e.target.value})} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="date" label="Vencimento" value={novoGasto.dataVencimento} onChange={(e) => setNovoGasto({...novoGasto, dataVencimento: e.target.value})} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Valor" value={novoGasto.valorFormatado} onChange={(e) => handleValorChange(e, (v) => setNovoGasto({...novoGasto, valorFormatado: v}))} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            <Grid item xs={12}><TextField fullWidth type="number" label="Nº de Parcelas" value={novoGasto.numParcelas} onChange={(e) => setNovoGasto({...novoGasto, numParcelas: e.target.value})} InputLabelProps={{ shrink: true }} inputProps={{ min: 1 }} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenNewExpense(false)}>Cancelar</Button>
          <Button variant="contained" onClick={adicionar} sx={{ bgcolor: '#1a237e' }}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default GastosMensais;
